import React, { MouseEvent, useState } from 'react';
import logo from 'src/assets/logo.png';
import { Box, Typography, IconButton, SvgIcon } from '@mui/material';
import * as Styled from './StyledComponents';
import { FmtpPage, UserMenuItem } from '../types';
import MenuIcon from '@mui/icons-material/Menu';
import NavMenuPopover from './NavMenuPopover';
import { PageNames } from 'src/util/types';
import { useFmtpNavigate } from 'src/hooks/useFmtpNavigate';
import { NavLink, useLocation } from 'react-router-dom';
import { addSessionId } from 'src/util/helpers';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Report } from '@lanterds/types';

export interface TopNavProps {
    selectedNavItem?: string;
    navLinks?: FmtpPage[];
    isDrawerOpen: boolean;
    toggleDrawer: () => void;
    userMenuClickHandler: (event: MouseEvent<HTMLElement>) => void;
    userDisplayName: string;
    anchorElement: null | HTMLElement;
    menuCloseHandler: () => void;
    logoutHandler: () => void;
    authorizedLabelsSubPages: PageNames[];
    reportsSubPages: Report[] | null;
}

export default function TopNav({
    selectedNavItem,
    navLinks,
    toggleDrawer,
    userMenuClickHandler,
    userDisplayName,
    anchorElement,
    menuCloseHandler,
    logoutHandler,
    authorizedLabelsSubPages,
    reportsSubPages,
}: TopNavProps) {
    const fmtpNavigate = useFmtpNavigate();
    // Logout is a unique case, which can be defined here. Concat additional menu items as needed.
    const userMenuItems: UserMenuItem[] = [
        {
            name: 'logout',
            displayText: 'Logout',
            handler: logoutHandler,
        },
    ];

    const [open, setOpen] = useState<HTMLElement | null>(null);
    const [openMenuItem, setOpenMenuItem] = useState<FmtpPage | null>(null);

    const handleOpen = (event: MouseEvent<HTMLElement>, linkItem: FmtpPage) => {
        let subPages: Report[] | FmtpPage[] | null | undefined = [];
        if (linkItem.name === PageNames.Reports) {
            event.preventDefault();
            subPages = reportsSubPages;
            setOpen(event.currentTarget);
            setOpenMenuItem(linkItem);
        } else {
            subPages = linkItem.subPages?.filter(
                (item) => !item.doNotDisplay && authorizedLabelsSubPages.find((pageName) => pageName === item.name),
            );

            if (subPages && subPages.length) {
                event.preventDefault();
                if (subPages.length > 1) {
                    setOpen(event.currentTarget);
                    setOpenMenuItem(linkItem);
                } else if (subPages.length === 1) {
                    fmtpNavigate(subPages[0].href);
                }
            }
        }
    };

    const handleClose = () => {
        setOpen(null);
        setOpenMenuItem(null);
    };

    const getNavPopover = (openMenuItem: FmtpPage) => {
        switch (openMenuItem.name) {
            case PageNames.Reports: {
                return (
                    <NavMenuPopover
                        open={open}
                        handleClose={handleClose}
                        pageName={openMenuItem.name}
                        items={reportsSubPages ?? []}
                    />
                );
            }
            case PageNames.ShippingPortal:
            default: {
                const displayableSubPages = openMenuItem.subPages?.filter((item) => !item.doNotDisplay) ?? [];
                const items = displayableSubPages.filter((item) =>
                    authorizedLabelsSubPages.some((pageName) => pageName === item.name),
                );
                if (items.length) {
                    return <NavMenuPopover open={open} handleClose={handleClose} items={items} />;
                }
                return null;
            }
        }
    };

    return (
        <Styled.AppBar position="static" id="navbar">
            <Styled.Toolbar>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Styled.NavMobileMenuIconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={toggleDrawer}
                    >
                        <MenuIcon />
                    </Styled.NavMobileMenuIconButton>

                    <Styled.NavLogoContainer>
                        <Box id="logo-container">
                            <img src={logo} alt="Lanter Logo" style={{ height: '75px', paddingBottom: '15px' }} />
                        </Box>
                    </Styled.NavLogoContainer>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 4,
                        height: '100%',
                    }}
                >
                    <Styled.NavLinksContainer>
                        {navLinks?.map((linkItem) => {
                            let subPages: Report[] | FmtpPage[] | null | undefined = [];
                            let hasSubPages = false;
                            const allHrefs = [linkItem.href, ...(linkItem.subPages?.map((sp) => sp.href) ?? [])];

                            const isActive = allHrefs.some(
                                (href) => location.pathname === href || location.pathname.startsWith(href + '/'),
                            );

                            if (linkItem.name === PageNames.Reports) {
                                hasSubPages = true;
                            } else {
                                subPages = linkItem.subPages?.filter(
                                    (item) =>
                                        !item.doNotDisplay &&
                                        authorizedLabelsSubPages.find((pageName) => pageName === item.name),
                                );
                                hasSubPages = !!subPages?.length;
                            }

                            return (
                                <IconButton
                                    key={linkItem.name}
                                    component={NavLink}
                                    to={addSessionId(linkItem.href)}
                                    id={linkItem.name}
                                    onClick={(event) => handleOpen(event, linkItem)}
                                    className={isActive ? 'selected' : ''}
                                    disableRipple
                                >
                                    {linkItem.icon && <SvgIcon component={linkItem.icon} />}
                                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                                        <Typography
                                            component="h6"
                                            variant="subtitle1"
                                            noWrap
                                            sx={{
                                                fontWeight: selectedNavItem === linkItem.name ? 'bold' : 'normal',
                                            }}
                                        >
                                            {linkItem.displayText}
                                        </Typography>
                                        {hasSubPages && linkItem.name !== 'userManagement' && (
                                            <KeyboardArrowDownIcon
                                                sx={{
                                                    // ml: 0.5,
                                                    fontSize: '1.2rem',
                                                }}
                                            />
                                        )}
                                    </Box>
                                </IconButton>
                            );
                        })}

                        {openMenuItem && getNavPopover(openMenuItem)}
                        <IconButton
                            // This id is used in cypress tests
                            id="user-menu-button"
                            aria-controls={anchorElement ? 'user-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={anchorElement ? 'true' : undefined}
                            onClick={userMenuClickHandler}
                            disableRipple={true}
                        >
                            <Typography
                                id="username"
                                component="h6"
                                variant="subtitle1"
                                noWrap
                                sx={{
                                    fontFamily: 'var(--nav-font-family)',
                                    color: 'var(--nav-text-color)',
                                    fontSize: '20px',
                                    paddingLeft: '10px',
                                }}
                            >
                                {userDisplayName}
                            </Typography>
                            <KeyboardArrowDownIcon
                                sx={{
                                    // ml: 0.5,
                                    fontSize: '1.2rem',
                                }}
                            />
                        </IconButton>
                        <NavMenuPopover
                            open={anchorElement}
                            handleClose={menuCloseHandler}
                            boxContainerClasses="user-menu-items"
                            popoverId="user-menu"
                            buttonId="logout-button"
                            items={userMenuItems}
                        />
                    </Styled.NavLinksContainer>
                </Box>
            </Styled.Toolbar>
        </Styled.AppBar>
    );
}
